import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Camera, CheckCircle2, ChevronDown, Loader2, X } from "lucide-react";
import { API, SITE, PARISHES, QUOTE_SERVICES, waLink } from "@/config/site";

const CONTACT_METHODS = ["Phone", "WhatsApp", "Email"];
const CONTACT_TIMES = ["Any time", "Morning (7am – 12pm)", "Afternoon (12pm – 4pm)", "Evening (4pm – 6pm)"];

const initialForm = {
  name: "", phone: "", email: "", location: "", service: "",
  message: "", contact_method: "Phone", contact_time: CONTACT_TIMES[0],
};

export default function QuoteForm() {
  const [form, setForm] = useState(initialForm);
  const [photos, setPhotos] = useState([]);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onPhotos = (e) => {
    const picked = Array.from(e.target.files || []);
    const room = 5 - photos.length;
    if (picked.length > room) {
      setError(room > 0 ? `You can upload up to 5 photos — ${room} more allowed.` : "You can upload up to 5 photos.");
      return;
    }
    setError("");
    setPhotos((p) => [...p, ...picked.slice(0, room).map((f) => ({ file: f, url: URL.createObjectURL(f) }))]);
    if (fileRef.current) fileRef.current.value = "";
  };

  const removePhoto = (i) => setPhotos((p) => p.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2 || form.phone.trim().length < 7) {
      setError("Please add your name and phone number.");
      return;
    }
    if (!form.location || !form.service) {
      setError("Please choose your parish and the service you need.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      photos.forEach((p) => fd.append("photos", p.file));
      await axios.post(`${API}/quotes`, fd);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong — please call or WhatsApp us instead.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="border border-joell-yellow/40 bg-joell-surface p-8 text-center sm:p-12" data-testid="quote-success-panel">
        <CheckCircle2 size={48} className="mx-auto text-joell-yellow" />
        <h3 className="mt-5 font-display text-2xl font-bold uppercase text-white sm:text-3xl">
          Thanks for contacting JOELL.
        </h3>
        <p className="mt-3 text-slate-300">We've received your request and will be in touch soon.</p>
        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <a href={SITE.phoneHref} className="btn-outline">Call {SITE.phone}</a>
          <a href={waLink("Hi JOELL, I just sent a quote request through your website.")} target="_blank" rel="noreferrer" className="btn-accent">
            Message us on WhatsApp
          </a>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="border border-white/10 bg-joell-surface p-6 sm:p-10" data-testid="quote-form" noValidate>
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="qf-name" className="field-label">Name *</label>
          <input id="qf-name" className="input-dark" placeholder="Your name" value={form.name} onChange={set("name")} data-testid="quote-form-input-name" />
        </div>
        <div>
          <label htmlFor="qf-phone" className="field-label">Phone Number *</label>
          <input id="qf-phone" type="tel" className="input-dark" placeholder="441-..." value={form.phone} onChange={set("phone")} data-testid="quote-form-input-phone" />
        </div>
        <div>
          <label htmlFor="qf-email" className="field-label">Email Address</label>
          <input id="qf-email" type="email" className="input-dark" placeholder="you@email.com" value={form.email} onChange={set("email")} data-testid="quote-form-input-email" />
        </div>
        <div>
          <label htmlFor="qf-location" className="field-label">Property Location / Parish *</label>
          <div className="relative">
            <select id="qf-location" className="input-dark appearance-none pr-10" value={form.location} onChange={set("location")} data-testid="quote-form-parish-select">
              <option value="">Select a parish…</option>
              {PARISHES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <ChevronDown size={16} className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-joell-yellow" />
          </div>
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="qf-service" className="field-label">Service Needed *</label>
          <div className="relative">
            <select id="qf-service" className="input-dark appearance-none pr-10" value={form.service} onChange={set("service")} data-testid="quote-form-service-select">
              <option value="">Select a service…</option>
              {QUOTE_SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <ChevronDown size={16} className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-joell-yellow" />
          </div>
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="qf-message" className="field-label">Tell Us About Your Project</label>
          <textarea id="qf-message" rows={4} className="input-dark resize-y" placeholder="e.g. Driveway is cracked and pooling water near the garage…" value={form.message} onChange={set("message")} data-testid="quote-form-input-message" />
        </div>
        <div className="sm:col-span-2">
          <span className="field-label">Preferred Contact Method</span>
          <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Preferred contact method">
            {CONTACT_METHODS.map((m) => (
              <button
                type="button"
                key={m}
                onClick={() => setForm((f) => ({ ...f, contact_method: m }))}
                className={`border px-3 py-3 font-mono text-[11px] font-bold uppercase tracking-widest transition-colors ${
                  form.contact_method === m
                    ? "border-joell-yellow bg-joell-yellow/10 text-joell-yellow"
                    : "border-white/15 text-slate-400 hover:border-white/40 hover:text-white"
                }`}
                data-testid={`quote-form-contact-method-${m.toLowerCase()}`}
              >
                {m}
              </button>
            ))}
          </div>
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="qf-time" className="field-label">Preferred Contact Time</label>
          <div className="relative">
            <select id="qf-time" className="input-dark appearance-none pr-10" value={form.contact_time} onChange={set("contact_time")} data-testid="quote-form-time-select">
              {CONTACT_TIMES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <ChevronDown size={16} className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-joell-yellow" />
          </div>
        </div>
        <div className="sm:col-span-2">
          <span className="field-label">Photos of the area (up to 5)</span>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="flex w-full flex-col items-center justify-center gap-2 border border-dashed border-white/25 bg-joell-coal/60 px-4 py-8 text-center transition-colors hover:border-joell-yellow"
            data-testid="quote-form-photo-upload-button"
          >
            <Camera size={22} className="text-joell-yellow" />
            <span className="text-sm text-slate-300">Tap to add photos of the area or repair needed</span>
            <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{photos.length} / 5 selected</span>
          </button>
          <input ref={fileRef} type="file" accept="image/*" multiple onChange={onPhotos} className="hidden" data-testid="quote-form-photo-input" />
          {photos.length > 0 && (
            <div className="mt-3 grid grid-cols-5 gap-2">
              {photos.map((p, i) => (
                <div key={p.url} className="relative aspect-square overflow-hidden border border-white/15">
                  <img src={p.url} alt={`Upload ${i + 1}`} className="h-full w-full object-cover" />
                  <button type="button" onClick={() => removePhoto(i)} className="absolute right-0.5 top-0.5 flex h-5 w-5 items-center justify-center bg-joell-black/85 text-white" aria-label={`Remove photo ${i + 1}`} data-testid={`quote-form-remove-photo-${i}`}>
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {error && (
        <p className="mt-5 border-l-2 border-red-500 bg-red-500/10 px-4 py-3 text-sm text-red-300" role="alert" data-testid="quote-form-error">
          {error}
        </p>
      )}

      <button type="submit" disabled={submitting} className="btn-accent mt-8 w-full !py-4 disabled:opacity-60" data-testid="quote-form-submit-button">
        {submitting ? <Loader2 size={16} className="animate-spin" /> : "Request My Quote"}
      </button>
      <p className="mt-4 text-center text-xs text-slate-500">
        Prefer to talk? Call <a href={SITE.phoneHref} className="text-joell-yellow">{SITE.phone}</a> or{" "}
        <Link to="/contact" className="text-joell-yellow">contact us</Link> another way.
      </p>
    </form>
  );
}
